import { useMemo, useState } from "react";

import { Autocomplete, Grid, TextField } from "@mui/material";
import { FormikTextField, toOptionItems, useFormikWithDirtyForm } from "@shared";
import { useQuery } from "@tanstack/react-query";

import { ContactDetailType, SendPortalInvitationRequest, clientPortalAccountsApi, clientsApi } from "@/api";
import { FormikAutocompleteSelect } from "@/common/components/fields/formik/formik-autocomplete/FormikAutocompleteSelect";
import { FormikDatePicker } from "@/common/components/fields/formik/formik-date-picker/FormikDatePicker";
import { PortalInvitationCard } from "@/common/components/portal-access/components/PortalInvitationCard";
import { useDropdownOptions } from "@/common/hooks/useDropdownOptions";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

import { IdentityDocumentFormValues, IdentityDocumentMaxLength } from "./ClientInvitationCard.types";
import { identityDocumentValidationSchema } from "./ClientInvitationCard.utils";
import { PortalEmailDuplicateHint } from "./PortalEmailDuplicateHint";

const initialIdentityDocument: IdentityDocumentFormValues = {
  type: null,
  number: "",
  issuingAuthority: "",
  validUntil: null,
};

interface Props {
  clientId: string;
  onSend: (request: SendPortalInvitationRequest) => void;
  isSending: boolean;
}

/**
 * The client flavour of the initial-activation card: pick one of the client's stored e-mail
 * addresses, optionally record the ID-document details (Ausweisdaten), and send the invitation. An
 * address that already has a portal account blocks the activation, so the same login can never be
 * claimed by two people. By customer decision the activation must stay possible without the
 * ID-document details.
 */
export function ClientInvitationCard({ clientId, onSend, isSending }: Props) {
  const { t } = useTranslation();
  const [selectedEmail, setSelectedEmail] = useState<string | null>(null);
  const { identityDocumentTypes } = useDropdownOptions();
  const identityDocumentTypeOptions = useMemo(() => toOptionItems(identityDocumentTypes), [identityDocumentTypes]);

  const { data: emailOptions = [] } = useQuery({
    queryKey: [QueryKey.ClientPersonalData, clientId],
    queryFn: () => clientsApi.getClientPersonalData({ clientId }),
    select: (data) =>
      data.contactDetails
        .filter((cd) => cd.type === ContactDetailType.Email && cd.value.trim() !== "")
        .map((cd) => cd.value),
  });

  // First address preselected without seeding state: the options load after the first render.
  const email = selectedEmail ?? emailOptions[0] ?? null;

  const { data: availability, isFetching } = useQuery({
    queryKey: [QueryKey.PortalEmailAvailability, clientId, email],
    queryFn: () => clientPortalAccountsApi.getPortalEmailAvailability({ clientId, email: email ?? "" }),
    enabled: email !== null,
  });

  const formik = useFormikWithDirtyForm<IdentityDocumentFormValues>({
    initialValues: initialIdentityDocument,
    validationSchema: identityDocumentValidationSchema(t),
    onSubmit: (values) => {
      if (!email) {
        return;
      }

      onSend({
        email,
        identityDocumentType: values.type,
        identityDocumentNumber: values.number,
        identityDocumentIssuingAuthority: values.issuingAuthority,
        identityDocumentValidUntil: values.validUntil,
      });
    },
  });

  const isBlocked = isFetching || availability === undefined || availability.portalAccountExists;

  return (
    <PortalInvitationCard
      description={t("clientAccess.invitation.description")}
      noEmailHint={t("clientAccess.invitation.noEmails")}
      hasEmail={emailOptions.length > 0}
      onSend={() => void formik.submitForm()}
      sendDisabled={!email || isBlocked}
      isSending={isSending}
    >
      <>
        <Grid container columnSpacing={2} sx={{ width: "100%" }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Autocomplete
              options={emailOptions}
              value={email}
              onChange={(_, value) => setSelectedEmail(value)}
              fullWidth
              renderInput={(params) => (
                <TextField {...params} label={t("clientAccess.invitation.emailLabel")} required size="small" />
              )}
            />
          </Grid>
        </Grid>

        {email && availability?.portalAccountExists && (
          <PortalEmailDuplicateHint email={email} availability={availability} />
        )}

        <Grid container columnSpacing={2} sx={{ width: "100%" }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormikAutocompleteSelect
              formik={formik}
              fieldName="type"
              label={t("clientAccess.invitation.identityDocument.type")}
              options={identityDocumentTypeOptions}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormikTextField
              formik={formik}
              fieldName="number"
              label={t("clientAccess.invitation.identityDocument.number")}
              slotProps={{ input: { inputProps: { maxLength: IdentityDocumentMaxLength.number } } }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormikTextField
              formik={formik}
              fieldName="issuingAuthority"
              label={t("clientAccess.invitation.identityDocument.issuingAuthority")}
              slotProps={{ input: { inputProps: { maxLength: IdentityDocumentMaxLength.issuingAuthority } } }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormikDatePicker
              formik={formik}
              fieldName="validUntil"
              label={t("clientAccess.invitation.identityDocument.validUntil")}
            />
          </Grid>
        </Grid>
      </>
    </PortalInvitationCard>
  );
}
